import * as React from "react";
import { UIModel } from "../../../model/ui-model";
import {
    Checkbox,
    CollectionPreferences,
    ColumnLayout,
    Flashbar,
    FlashbarProps,
    Header,
    Select,
    SpaceBetween,
    Toggle
} from "@amzn/awsui-components-react";
import { UIField } from "../../common/UIField";
import { WhosMyLawyerFactory } from "../../../factory/whos-my-lawyer-factory";
import { WhosMyLawyerContent, ConfigurableOption } from "@amzn/ask-legal-domain";
import { LegalContactsPolarisFactory } from "../../../factory/polaris/legal-contacts-polaris-factory";
import { LegalContactFieldsConfigPanel } from "../../admin/LegalContactFieldsConfigPanel";
import { AppContext } from "../../../setup/context";
import { Preference } from "../../../setup/preference";

export const WhosMyLawyerContentEdit = (props: {
    state: UIModel.State<WhosMyLawyerContent>;
}) => {
    const context = React.useContext(AppContext);
    const wmlAutoAddContactsLab = context.hasLab(Preference.Lab.WMLAutoAddContacts);
    const [autoAddFlashItems, setAutoAddFlashItems] = React.useState<FlashbarProps.MessageDefinition[]>([]);

    const columnDisplayOptions = LegalContactsPolarisFactory.Table.getColumnDisplayOptions(props.state.value);
    const columnDisplay = props.state.value?.defaultConfiguration?.columnDisplay;

    return <>
        <hr />
        {wmlAutoAddContactsLab && (
            <>
                <Header variant="h3">Auto Add Contacts</Header>
                <SpaceBetween size="s">
                    <Toggle
                        description="Automatically add new legal contacts to this container when their manager is already listed as a contact"
                        checked={props.state.value?.autoAddNewContacts ?? false}
                        onChange={({ detail }) => {
                            const wasEnabled = props.state.value?.autoAddNewContacts;
                            props.state.setValue(
                                WhosMyLawyerContent.update(props.state.value, {
                                    autoAddNewContacts: detail.checked,
                                    ...(!detail.checked && { includeContingentWorkers: false })
                                })
                            );
                            if (detail.checked && !wasEnabled) {
                                setAutoAddFlashItems([{
                                    type: "info",
                                    content: "Auto-add enabled. Save or publish this container to start the initial sync.",
                                    dismissible: true,
                                    onDismiss: () => setAutoAddFlashItems([])
                                }]);
                            } else {
                                setAutoAddFlashItems([]);
                            }
                        }}
                    >
                        Enable Auto Add Contacts Feature
                    </Toggle>
                    {autoAddFlashItems.length > 0 && (
                        <Flashbar items={autoAddFlashItems} />
                    )}
                    {props.state.value?.autoAddNewContacts && (
                        <Toggle
                            description="When disabled, contingent workers (yellow and green badge) are excluded from auto-add"
                            checked={props.state.value?.includeContingentWorkers ?? false}
                            onChange={({ detail }) => props.state.setValue(
                                WhosMyLawyerContent.update(props.state.value, {
                                    includeContingentWorkers: detail.checked
                                })
                            )}
                        >
                            Include contingent workers
                        </Toggle>
                    )}
                </SpaceBetween>
                <hr />
            </>
        )}
        <Header variant="h3">Default Table View Configuration</Header>
        <ColumnLayout columns={2}>
            <UIField.CustomField
                name="Sorting Field"
                child={<Select
                    selectedOption={WhosMyLawyerFactory.toOptionDefinition(
                        props.state.value?.defaultConfiguration.sorting?.field,
                        props.state.value
                    )}
                    onChange={({ detail }) => props.state.setValue(
                        WhosMyLawyerContent.update(props.state.value, {
                            configuration: {
                                sorting: {
                                    field: detail.selectedOption.value,
                                    order: props.state.value.defaultConfiguration.sorting.order
                                }
                            }
                        })
                    )}
                    options={WhosMyLawyerFactory.getSortSelectOptions(props.state.value)}
                />}
            />
            <UIField.CustomField
                name="Sorting Order"
                child={<Checkbox
                    checked={props.state.value?.defaultConfiguration.sorting?.order === "ASC"}
                    onChange={({ detail }) => props.state.setValue(
                        WhosMyLawyerContent.update(props.state.value, {
                            configuration: {
                                sorting: {
                                    field: props.state.value.defaultConfiguration.sorting.field,
                                    order: detail.checked ? "ASC" : "DESC"
                                }
                            }
                        })
                    )}
                >
                    isAscending
                </Checkbox>}
            />
            {(() => {
                const visibleColumnOptions = (columnDisplay?.filter(col => col.visible) ?? columnDisplayOptions)
                    .flatMap(col => {
                        const opt = columnDisplayOptions.find(c => c.id === col.id);
                        return opt ? [{ label: opt.label, value: opt.id }] : [];
                    });
                const primaryFieldId = columnDisplay?.find(col => col.visible)?.id;
                return (
                    <UIField.CustomField
                        name="Primary field"
                        child={<Select
                            selectedOption={visibleColumnOptions.find(opt => opt.value === primaryFieldId) || visibleColumnOptions[0]}
                            onChange={({ detail }) => {
                                // Get existing column display setting or set default setting
                                const currentColumnDisplay = columnDisplay ||
                                    LegalContactsPolarisFactory.Table.getDefaultColumnDisplay(props.state.value);
                                const idx = currentColumnDisplay.findIndex(col => col.id === detail.selectedOption.value);
                                if (idx > 0) {
                                    const reordered = [...currentColumnDisplay];
                                    reordered.unshift(reordered.splice(idx, 1)[0]);
                                    props.state.setValue(WhosMyLawyerContent.update(props.state.value, {
                                        configuration: { columnDisplay: reordered }
                                    }));
                                }
                            }}
                            options={visibleColumnOptions}
                        />}
                    />
                );
            })()}
            <UIField.LabelField
                label="Manage View Preferences"
                children={<CollectionPreferences
                    preferences={
                        WhosMyLawyerFactory.toPreferencesFromDisplayConfiguration(
                            props.state.value?.defaultConfiguration,
                            props.state.value
                        )
                    }
                    onConfirm={({ detail }) => props.state.setValue(
                        WhosMyLawyerContent.update(props.state.value, {
                            configuration: WhosMyLawyerFactory.toDisplayConfigurationFromPreferences(
                                detail,
                                props.state.value.defaultConfiguration
                            )
                        })
                    )}
                    pageSizePreference={{
                        options: LegalContactsPolarisFactory.DEFAULT_PAGE_SIZE_OPTIONS,
                        title: "Number of contacts per page"
                    }}
                    stripedRowsPreference={{
                        label: "Striped rows",
                        description: "Select to add alternating shaded rows"
                    }}
                    contentDisplayPreference={{
                        options: LegalContactsPolarisFactory.Table.getColumnDisplayOptions(props.state.value),
                        title: "Default column view preferences",
                        description: "Customize the columns visibility and order"
                    }}
                    cancelLabel="Cancel"
                    confirmLabel="Save"
                    title="Set default table preferences"
                />}
            />
        </ColumnLayout>
        <hr />
        <LegalContactFieldsConfigPanel
            scopeOptions={props.state.value?.dataOptions?.scopeOptions || []}
            teamOptions={props.state.value?.dataOptions?.teamOptions || []}
            subTeamOptions={props.state.value?.dataOptions?.subTeamOptions || []}
            additionalFieldsConfig={props.state.value?.dataOptions?.additionalFieldsConfig || []}
            onScopeOptionsChange={(updatedOptions) => {
                props.state.setValue(
                    WhosMyLawyerContent.update(props.state.value, {
                        dataOptions: { scopeOptions: updatedOptions }
                    })
                );
            }}
            onTeamOptionsChange={(updatedOptions) => {
                props.state.setValue(
                    WhosMyLawyerContent.update(props.state.value, {
                        dataOptions: { teamOptions: updatedOptions }
                    })
                );
            }}
            onSubTeamOptionsChange={(updatedOptions) => {
                props.state.setValue(
                    WhosMyLawyerContent.update(props.state.value, {
                        dataOptions: { subTeamOptions: updatedOptions }
                    })
                );
            }}
            onAdditionalFieldsChange={(newConfig) => {
                props.state.setValue(
                    WhosMyLawyerContent.update(props.state.value, {
                        dataOptions: {
                            additionalFieldsConfig: newConfig
                        }
                    })
                );
            }}
        />
    </>;
};
